/**
 * TrialBanner — shown at the top of Dashboard when user is on an active trial plan.
 *
 * Props:
 *   planStatus — object from GET /api/billing/status
 */
import { useState } from 'react';
import { X, Zap, AlertTriangle, Clock, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PlanSelectionModal from '@/components/PlanSelectionModal';

export default function TrialBanner({ planStatus }) {
  const [dismissed, setDismissed] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (!planStatus || dismissed) return null;

  const { has_plan, is_paid, generations_remaining, executions_remaining } = planStatus;

  // Only show for free (non-paid) users with credit info.
  if (!has_plan || is_paid || generations_remaining === null || generations_remaining === undefined) {
    return null;
  }

  const genLeft = generations_remaining;
  const runLeft = executions_remaining;
  const minLeft = Math.min(genLeft, runLeft);

  // Colour theme based on how much is left
  let bg, border, textColor, icon;
  if (minLeft >= 3) {
    bg = 'bg-[#00E599]/10';
    border = 'border-[#00E599]/30';
    textColor = 'text-[#00E599]';
    icon = <Zap className="w-4 h-4 text-[#00E599] flex-shrink-0" />;
  } else if (minLeft >= 1) {
    bg = 'bg-yellow-500/10';
    border = 'border-yellow-500/30';
    textColor = 'text-yellow-400';
    icon = <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  } else {
    bg = 'bg-orange-500/10';
    border = 'border-orange-500/30';
    textColor = 'text-orange-400';
    icon = <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />;
  }

  const message =
    minLeft >= 1
      ? `Free plan — ${genLeft} generation${genLeft === 1 ? '' : 's'} and ${runLeft} run${runLeft === 1 ? '' : 's'} left this month. Upgrade for unlimited usage.`
      : `You've used your free credits this month (generations: ${genLeft}, runs: ${runLeft}). Upgrade to keep going.`;

  return (
    <>
      <div className={`w-full ${bg} border ${border} rounded-lg px-4 py-2.5 flex items-center gap-3`}>
        {icon}
        <p className={`text-sm flex-1 ${textColor}`}>{message}</p>
        <Button
          size="sm"
          className="bg-[#00E599] text-black hover:bg-[#00CC88] font-semibold text-xs h-7 px-3 flex-shrink-0 flex items-center gap-1"
          onClick={() => setShowUpgrade(true)}
        >
          <Rocket className="w-3 h-3" />
          Upgrade
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <PlanSelectionModal
        open={showUpgrade}
        dismissible
        showFree={false}
        onClose={() => setShowUpgrade(false)}
      />
    </>
  );
}
